import {Checkbox, Chip, CircularProgress} from '@mui/material'
import {GridRowId} from '@mui/x-data-grid-pro'
import {envImagePath} from 'GLOBAL/envVariables'
import {
  customerStatusHandler,
  globalFormatWithHyphens,
  globalSerialNumberFormatWithHyphens,
} from 'utils/const/globalConst'

interface Props {
  rows: any[]
  loading: boolean
  selectionModel: GridRowId[]
  onSelectionChange: (ids: GridRowId[], selectedRows: any[]) => void
  onImageDetailOpen: (row: any) => void
  onPhotoOpen: (row: any) => void
  onCustomerDetailOpen: (row: any) => void
  usePhotoModal: boolean
  cooperInfo: any
}

const CustomerInfoMobileCard = ({
  rows,
  loading,
  selectionModel,
  onSelectionChange,
  onImageDetailOpen,
  onPhotoOpen,
  onCustomerDetailOpen,
  usePhotoModal,
  cooperInfo,
}: Props) => {
  const isSelected = (id: GridRowId) => selectionModel.includes(id)

  const toggleSelect = (row: any) => {
    const id = row.id
    const newIds = isSelected(id)
      ? selectionModel.filter((s) => s !== id)
      : [...selectionModel, id]
    const selectedRows = rows.filter((r) => newIds.includes(r.id))
    onSelectionChange(newIds, selectedRows)
  }

  if (loading) {
    return (
      <div className='flex justify-center p-8'>
        <CircularProgress />
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-2 mt-2'>
      {rows.map((row) => {
        const selected = isSelected(row.id)
        const customerNum = globalFormatWithHyphens(row.officeId, row.customerNumber)
        const serialNum = globalSerialNumberFormatWithHyphens(row.serialNumber)
        const cooperIdx = cooperInfo?.value
        const imageSrc = row.filePath ? `${envImagePath}${row.filePath}` : null
        const asStatus = customerStatusHandler(row.customerStatus)

        return (
          <div
            key={row.id}
            className={`border rounded-lg shadow-sm overflow-hidden ${
              selected ? 'border-blue-500 bg-blue-50' : 'border-slate-300 bg-white'
            }`}
          >
            {/* 헤더: 체크박스 + 수용가번호 + A/S 상태 / 우측 끝 사업소 */}
            <div
              className={`flex items-center gap-2 px-3 py-2 border-b ${
                selected ? 'border-blue-300 bg-blue-100' : 'border-slate-300 bg-slate-50'
              }`}
            >
              <Checkbox
                checked={selected}
                onChange={() => toggleSelect(row)}
                sx={{p: 0.5, '& .MuiSvgIcon-root': {fontSize: 32}}}
              />
              <span
                className='text-blue-700 font-bold cursor-pointer text-3xl'
                onClick={() => (usePhotoModal ? onPhotoOpen(row) : onCustomerDetailOpen(row))}
              >
                {customerNum}
              </span>
              {asStatus && (
                <Chip label={asStatus} color='error' sx={{fontSize: '1rem'}} className='shrink-0' />
              )}
              {row.officeName && (
                <span className='text-lg text-slate-700 ml-auto min-w-0 truncate'>
                  {row.officeName}
                </span>
              )}
            </div>

            {/* 본문: 좌측 정보 + 우측 썸네일 */}
            <div className='flex gap-3 p-4'>
              <div className='flex-1 min-w-0 flex flex-col gap-1.5'>
                {/* 이름 */}
                <div className='text-2xl font-bold text-slate-900 line-clamp-2'>
                  {row.customerName}
                </div>

                {/* 주소 */}
                <div className='text-xl text-slate-800 line-clamp-2'>
                  <span className='text-blue-600 font-bold'>도로명</span> {row.addressRoad}
                </div>
                {row.addressNumber && (
                  <div className='text-xl text-slate-800 line-clamp-2'>
                    <span className='text-orange-600 font-bold'>지번</span> {row.addressNumber}
                  </div>
                )}
                {row.managerName && row.managerName !== 'NULL' && (
                  <div className='text-xl text-slate-700 line-clamp-2'>
                    담당자: {row.managerName}
                  </div>
                )}
                {row.asInstallerName && (
                  <div className='text-xl text-slate-700 line-clamp-2'>
                    할당: {row.asInstallerName}
                  </div>
                )}

                {/* 단말기 SN + 배터리 */}
                <div className='flex items-center gap-2 text-xl mt-1 flex-wrap'>
                  <span className='text-slate-800'>{serialNum}</span>
                  {(cooperIdx === 1 || cooperIdx === 0) && row.batteryLevel != null && (
                    <span
                      className={`font-semibold ${
                        row.batteryLevel < 30 ? 'text-red-600' : 'text-slate-700'
                      }`}
                    >
                      배터리 {row.batteryLevel}%
                    </span>
                  )}
                </div>

                {/* 통신상태 + 구경 */}
                <div className='flex items-center gap-x-4 gap-y-1 mt-1 text-xl flex-wrap'>
                  <div className='flex items-center gap-1'>
                    <span className='text-slate-700'>계량기</span>
                    <span
                      className={`font-bold ${row.efosStatus ? 'text-green-700' : 'text-red-600'}`}
                    >
                      {row.efosStatus ? '정상' : '점검'}
                    </span>
                  </div>
                  <div className='flex items-center gap-1'>
                    <span className='text-slate-700'>단말기</span>
                    <span
                      className={`font-bold ${row.deviceStatus ? 'text-green-700' : 'text-red-600'}`}
                    >
                      {row.deviceStatus ? '정상' : '점검'}
                    </span>
                  </div>
                  {row.meterDiameter && <span className='text-slate-700'>{row.meterDiameter}mm</span>}
                  {row.temperature != null && row.temperature < 90 && (
                    <span className='text-slate-700'>{row.temperature}°C</span>
                  )}
                </div>
              </div>

              {/* 이미지 썸네일 */}
              {imageSrc && (
                <div className='shrink-0 self-center' style={{width: '38%', maxWidth: 150}}>
                  <img
                    src={imageSrc}
                    alt='meter'
                    className='w-full h-auto object-contain rounded cursor-pointer border-2 border-slate-400'
                    onClick={() => onImageDetailOpen(row)}
                  />
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default CustomerInfoMobileCard
